import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './DetailUser.css';

const API_URL = import.meta.env.VITE_API_URL;
const getApiUrl = (ep) => {
  const base = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;
  return `${base}${ep}`;
};
const getToken = () => localStorage.getItem('token');

const ROLE_CONFIG = {
  admin:        { label: 'Administrateur', color: '#1e3a5f',  bg: '#dbeafe', text: '#1e40af', icon: '👑' },
  moderateur:   { label: 'Modérateur',     color: '#6b46c1',  bg: '#ede9fe', text: '#5b21b6', icon: '🛡️' },
  commercial:   { label: 'Commercial',     color: '#276749',  bg: '#c6f6d5', text: '#276749', icon: '💼' },
  installateur: { label: 'Installateur',   color: '#c05621',  bg: '#fef3c7', text: '#92400e', icon: '🔧' },
  client:       { label: 'Client',         color: '#2c7a7b',  bg: '#e6fffa', text: '#234e52', icon: '👤' },
  entreprise:   { label: 'Entreprise',     color: '#6b46c1',  bg: '#faf5ff', text: '#553c9a', icon: '🏢' },
  user:         { label: 'Utilisateur',    color: '#4a5568',  bg: '#f7fafc', text: '#2d3748', icon: '👤' },
};

const ACTION_LABEL = {
  CREATE_CLIENT:      '➕ Création client',
  UPDATE_CLIENT:      '✏️ Modification client',
  DELETE_CLIENT:      '🗑️ Suppression client',
  ADD_NOTE_CLIENT:    '📝 Note client',
  CREATE_DOSSIER:     '📁 Création dossier',
  UPDATE_DOSSIER:     '✏️ Modification dossier',
  CHANGE_ETAT_DOSSIER:'🔄 Changement état',
  ADD_PAYMENT:        '💰 Paiement ajouté',
  UPDATE_PAYMENT:     '💳 Paiement modifié',
  DELETE_PAYMENT:     '💸 Paiement supprimé',
  CREATE_USER:        '👤 Création utilisateur',
  UPDATE_USER:        '✏️ Modification utilisateur',
  DELETE_USER:        '🗑️ Suppression utilisateur',
  LOGIN:              '🔐 Connexion',
  LOGOUT:             '🔓 Déconnexion',
  LOGIN_FAILED:       '⚠️ Échec connexion',
  PASSWORD_CHANGED:   '🔑 Mot de passe modifié',
};

const ACTION_COLORS = {
  CREATE_CLIENT: '#276749', UPDATE_CLIENT: '#1d4ed8', DELETE_CLIENT: '#c53030',
  ADD_NOTE_CLIENT: '#553c9a', CREATE_DOSSIER: '#276749', CHANGE_ETAT_DOSSIER: '#c05621',
  ADD_PAYMENT: '#276749', UPDATE_PAYMENT: '#1d4ed8', DELETE_PAYMENT: '#c53030',
  LOGIN: '#276749', LOGOUT: '#4a5568', LOGIN_FAILED: '#c53030',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatDuration = (s) => {
  if (!s) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${sec}s`;
  return `${sec}s`;
};

const getInitials = (prenom, nom) => {
  return `${(prenom || '')[0] || ''}${(nom || '')[0] || ''}`.toUpperCase();
};

const DetailUser = ({ user, onClose }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [userLogs, setUserLogs] = useState([]);
  const [userSessions, setUserSessions] = useState([]);
  const [isOnline, setIsOnline] = useState(false);
  const [activeSessionData, setActiveSessionData] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const roleConf = ROLE_CONFIG[user?.typeUser] || ROLE_CONFIG.user;

  const checkOnlineStatus = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl('/audit/sessions/active'), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const sessions = res.data.sessions || [];
      const mySession = sessions.find(s =>
        String(s.userId?._id || s.userId) === String(user._id)
      );
      setIsOnline(!!mySession);
      setActiveSessionData(mySession || null);
    } catch { setIsOnline(false); }
  }, [user._id]);

  const fetchUserLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(getApiUrl(`/audit/logs/user/${user._id}?limit=30`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setUserLogs(res.data.logs || []);
    } catch { }
    finally { setLoadingLogs(false); }
  }, [user._id]);

  const fetchUserSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await axios.get(getApiUrl(`/audit/sessions/user/${user._id}?limit=20`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setUserSessions(res.data.sessions || []);
    } catch { }
    finally { setLoadingSessions(false); }
  }, [user._id]);

  useEffect(() => {
    checkOnlineStatus();
    const timer = setInterval(checkOnlineStatus, 20000);
    return () => clearInterval(timer);
  }, [checkOnlineStatus]);

  useEffect(() => {
    if (activeTab === 'historique') fetchUserLogs();
    if (activeTab === 'sessions') fetchUserSessions();
  }, [activeTab, fetchUserLogs, fetchUserSessions]);

  if (!user) return null;

  const tabs = [
    { id: 'info',       label: 'Informations',  icon: '📋' },
    { id: 'historique', label: 'Historique',     icon: '📜' },
    { id: 'sessions',   label: 'Sessions',       icon: '🔐' },
  ];

  const handleUnban = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir débloquer ${user.prenom} ${user.nom} ?`)) return;
    try {
      await axios.post(getApiUrl(`/users/${user._id}/unban`), {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      alert('Utilisateur débloqué avec succès !');
      // Idéalement, il faudrait recharger les données de l'utilisateur ici
      if (typeof window !== 'undefined') window.location.reload();
    } catch (err) {
      alert("Erreur lors du déblocage: " + (err.response?.data?.message || err.message));
    }
  };

  const handleForceLogout = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir déconnecter de force ${user.prenom} ${user.nom} ?`)) return;
    try {
      await axios.post(getApiUrl(`/users/${user._id}/force-logout`), {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      alert('Utilisateur déconnecté avec succès !');
      checkOnlineStatus();
      fetchUserSessions();
      fetchUserLogs();
    } catch (err) {
      alert("Erreur lors de la déconnexion forcée: " + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div className="du-overlay" onClick={onClose}>
      <div className="du-card" onClick={e => e.stopPropagation()}>

        {/* ══════════════════════════════════════════════
            HERO CARD — Avatar + Identité + Statuts
        ══════════════════════════════════════════════ */}
        <div className="du-hero">

          {/* Fond dégradé hero */}
          <div className="du-hero-bg" style={{ background: `linear-gradient(135deg, ${roleConf.color}18, ${roleConf.color}08)` }} />

          {/* Close button */}
          <button className="du-close-btn" onClick={onClose} title="Fermer">✕</button>

          {/* Avatar */}
          <div className="du-avatar-wrap">
            <div className="du-avatar" style={{ background: roleConf.color }}>
              {getInitials(user.prenom, user.nom)}
            </div>
            {/* Badge statut en ligne */}
            <span className={`du-online-ring ${isOnline ? 'online' : 'offline'}`} />
          </div>

          {/* Identité */}
          <div className="du-hero-identity">
            <h2 className="du-fullname">{user.prenom} {user.nom}</h2>
            <p className="du-email">{user.mail}</p>

            {/* Badges rang */}
            <div className="du-hero-badges">
              <span className="du-role-badge"
                style={{ background: roleConf.bg, color: roleConf.text, borderColor: `${roleConf.color}30` }}>
                {roleConf.icon} {roleConf.label}
              </span>

              <span className={`du-status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                {user.isActive ? '✅ Compte actif' : '❌ Compte désactivé'}
              </span>

              <span className={`du-online-badge ${isOnline ? 'online' : 'offline'}`}>
                <span className={`du-dot ${isOnline ? 'pulsing' : ''}`} />
                {isOnline ? 'En ligne maintenant' : 'Hors ligne'}
              </span>
            </div>
          </div>

          {/* Quick stats row */}
          <div className="du-quick-stats">
            <div className="du-qs-item">
              <span className="du-qs-value">{userLogs.length || '—'}</span>
              <span className="du-qs-label">Actions</span>
            </div>
            <div className="du-qs-divider" />
            <div className="du-qs-item">
              <span className="du-qs-value">{userSessions.length || '—'}</span>
              <span className="du-qs-label">Sessions</span>
            </div>
            <div className="du-qs-divider" />
            <div className="du-qs-item">
              <span className="du-qs-value">{formatDate(user.lastLoginAt).split(' ')[0] || '—'}</span>
              <span className="du-qs-label">Dernière connexion</span>
            </div>
            {activeSessionData && (
              <>
                <div className="du-qs-divider" />
                <div className="du-qs-item">
                  <span className="du-qs-value" style={{ color: '#276749' }}>🟢 {activeSessionData.deviceType || 'PC'}</span>
                  <span className="du-qs-label">{activeSessionData.ipAddress}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════════
            TABS
        ══════════════════════════════════════════════ */}
        <div className="du-tabs-bar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`du-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="du-tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════
            TAB CONTENT
        ══════════════════════════════════════════════ */}
        <div className="du-body">

          {/* ── INFO ─────────────────────────────────── */}
          {activeTab === 'info' && (
            <div className="du-info-section">

              {/* Bloc : Coordonnées */}
              <div className="du-info-block">
                <div className="du-block-title">
                  <span className="du-block-icon">👤</span>
                  Coordonnées
                </div>
                <div className="du-fields-grid">
                  <div className="du-field">
                    <span className="du-field-label">Prénom</span>
                    <span className="du-field-value">{user.prenom || '—'}</span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Nom</span>
                    <span className="du-field-value">{user.nom || '—'}</span>
                  </div>
                  <div className="du-field du-field-wide">
                    <span className="du-field-label">Email</span>
                    <span className="du-field-value du-monospace">{user.mail || '—'}</span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Téléphone</span>
                    <span className="du-field-value">{user.numeroTelephone || '—'}</span>
                  </div>
                  <div className="du-field du-field-wide">
                    <span className="du-field-label">Adresse</span>
                    <span className="du-field-value">{user.adresse || '—'}</span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Région</span>
                    <span className="du-field-value">{user.region || '—'}</span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Ville</span>
                    <span className="du-field-value">{user.ville || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Bloc : Sécurité & Accès */}
              <div className="du-info-block">
                <div className="du-block-title">
                  <span className="du-block-icon">🔒</span>
                  Sécurité & Accès
                </div>
                <div className="du-fields-grid">
                  <div className="du-field">
                    <span className="du-field-label">Rôle</span>
                    <span className="du-field-value">
                      <span className="du-inline-badge" style={{ background: roleConf.bg, color: roleConf.text }}>
                        {roleConf.icon} {roleConf.label}
                      </span>
                    </span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Statut compte</span>
                    <span className="du-field-value">
                      <span className={`du-inline-badge ${user.isActive ? 'badge-green' : 'badge-red'}`}>
                        {user.isActive ? '✅ Actif' : '❌ Inactif'}
                      </span>
                    </span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Connexion actuelle</span>
                    <span className="du-field-value" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span className={`du-inline-badge ${isOnline ? 'badge-green' : 'badge-gray'}`}>
                        <span className={`du-dot-sm ${isOnline ? 'pulsing' : ''}`} />
                        {isOnline ? 'En ligne' : 'Hors ligne'}
                      </span>
                      {isOnline && (
                        <button 
                          onClick={handleForceLogout}
                          style={{ padding: '4px 8px', fontSize: '11px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer' }}
                          title="Invalide la session et déconnecte immédiatement"
                        >
                          Forcer déconnexion
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">État du compte (Rate Limit)</span>
                    <span className="du-field-value" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {user.lockUntil && new Date(user.lockUntil) > new Date() ? (
                        <>
                          <span className="du-inline-badge badge-red">
                            🔒 Bloqué jusqu'à {new Date(user.lockUntil).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <button 
                            onClick={handleUnban}
                            style={{ padding: '4px 8px', fontSize: '11px', background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            🔓 Débloquer
                          </button>
                        </>
                      ) : (
                        <span className="du-inline-badge badge-green">
                          ✅ Non bloqué
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Dernière connexion</span>
                    <span className="du-field-value">{formatDate(user.lastLoginAt)}</span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">IP dernière connexion</span>
                    <span className="du-field-value du-monospace">{user.lastLoginIp || '—'}</span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Créé le</span>
                    <span className="du-field-value">{formatDate(user.createdAt)}</span>
                  </div>
                  <div className="du-field">
                    <span className="du-field-label">Modifié le</span>
                    <span className="du-field-value">{formatDate(user.updatedAt)}</span>
                  </div>
                  {user.totalClients > 0 && (
                    <div className="du-field">
                      <span className="du-field-label">Total clients</span>
                      <span className="du-field-value du-highlight">{user.totalClients}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Bloc : Permissions (modérateur) */}
              {user.typeUser === 'moderateur' && user.permissions?.length > 0 && (
                <div className="du-info-block du-perms-block">
                  <div className="du-block-title">
                    <span className="du-block-icon">🔑</span>
                    Permissions accordées
                    <span className="du-perms-count">{user.permissions.length}</span>
                  </div>
                  <div className="du-perms-grid">
                    {user.permissions.map(p => {
                      const [mod, act] = p.split(':');
                      const actColors = {
                        read: { bg: '#dbeafe', color: '#1d4ed8' },
                        create: { bg: '#dcfce7', color: '#166534' },
                        update: { bg: '#fef3c7', color: '#92400e' },
                        delete: { bg: '#fee2e2', color: '#991b1b' },
                      };
                      const c = actColors[act] || { bg: '#f1f5f9', color: '#475569' };
                      return (
                        <div key={p} className="du-perm-chip">
                          <span className="du-perm-module">{mod}</span>
                          <span className="du-perm-action" style={{ background: c.bg, color: c.color }}>{act}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── HISTORIQUE ───────────────────────────── */}
          {activeTab === 'historique' && (
            <div className="du-logs-section">
              <div className="du-section-header">
                <span className="du-section-title">
                  Journal des actions
                  {userLogs.length > 0 && <span className="du-count-badge">{userLogs.length}</span>}
                </span>
                <button className="du-refresh-btn" onClick={fetchUserLogs} title="Actualiser">
                  🔄 Actualiser
                </button>
              </div>

              {loadingLogs ? (
                <div className="du-loading-state">
                  <div className="du-spinner" />
                  <span>Chargement du journal...</span>
                </div>
              ) : userLogs.length === 0 ? (
                <div className="du-empty-state">
                  <div className="du-empty-icon">📭</div>
                  <p>Aucune action enregistrée</p>
                  <span>Les actions de cet utilisateur apparaîtront ici.</span>
                </div>
              ) : (
                <div className="du-timeline">
                  {userLogs.map((log, idx) => {
                    const emoji = (ACTION_LABEL[log.action] || '• Autre').split(' ')[0];
                    const label = (ACTION_LABEL[log.action] || log.action).split(' ').slice(1).join(' ');
                    const color = ACTION_COLORS[log.action] || '#718096';
                    return (
                      <div key={log._id} className="du-timeline-item">
                        {idx < userLogs.length - 1 && <div className="du-tl-line" />}
                        <div className="du-tl-dot" style={{ background: `${color}18`, border: `2px solid ${color}` }}>
                          <span style={{ fontSize: 12 }}>{emoji}</span>
                        </div>
                        <div className="du-tl-card">
                          <div className="du-tl-top">
                            <span className="du-tl-action" style={{ color }}>{label}</span>
                            <span className="du-tl-time">{formatDate(log.timestamp)}</span>
                          </div>
                          {log.target?.label && (
                            <div className="du-tl-meta">
                              <span className="du-tl-target-type">{log.target.type}</span>
                              <span className="du-tl-target-label">{log.target.label}</span>
                            </div>
                          )}
                          {log.comment && (
                            <div className="du-tl-comment">{log.comment}</div>
                          )}
                          {log.changes?.length > 0 && (
                            <div className="du-tl-changes-badge">
                              {log.changes.length} champ{log.changes.length > 1 ? 's' : ''} modifié{log.changes.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── SESSIONS ─────────────────────────────── */}
          {activeTab === 'sessions' && (
            <div className="du-sessions-section">
              <div className="du-section-header">
                <span className="du-section-title">
                  Historique des sessions
                  {userSessions.length > 0 && <span className="du-count-badge">{userSessions.length}</span>}
                </span>
                <button className="du-refresh-btn" onClick={fetchUserSessions}>🔄 Actualiser</button>
              </div>

              {loadingSessions ? (
                <div className="du-loading-state">
                  <div className="du-spinner" />
                  <span>Chargement des sessions...</span>
                </div>
              ) : userSessions.length === 0 ? (
                <div className="du-empty-state">
                  <div className="du-empty-icon">🔐</div>
                  <p>Aucune session enregistrée</p>
                </div>
              ) : (
                <div className="du-sessions-list">
                  {userSessions.map(s => (
                    <div key={s._id} className={`du-session-card ${s.status}`}>
                      <div className="du-sc-status">
                        <span className={`du-sc-dot ${s.status}`} />
                        <span className={`du-sc-label ${s.status}`}>
                          {s.status === 'active' ? 'En ligne' : s.status === 'closed' ? 'Terminée' : 'Expirée'}
                        </span>
                      </div>
                      <div className="du-sc-body">
                        <div className="du-sc-row">
                          <div className="du-sc-info">
                            <span className="du-sc-icon">🔐</span>
                            <div>
                              <div className="du-sc-key">Connexion</div>
                              <div className="du-sc-val">{formatDate(s.loginAt)}</div>
                            </div>
                          </div>
                          {s.logoutAt && (
                            <div className="du-sc-info">
                              <span className="du-sc-icon">🔓</span>
                              <div>
                                <div className="du-sc-key">Déconnexion</div>
                                <div className="du-sc-val">{formatDate(s.logoutAt)}</div>
                              </div>
                            </div>
                          )}
                          {s.duration && (
                            <div className="du-sc-info">
                              <span className="du-sc-icon">⏱️</span>
                              <div>
                                <div className="du-sc-key">Durée</div>
                                <div className="du-sc-val du-sc-duration">{formatDuration(s.duration)}</div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="du-sc-device">
                          <span className="du-sc-device-icon">💻</span>
                          <span>{s.deviceType || 'PC'}</span>
                          <span className="du-sc-sep">·</span>
                          <span>{s.browser || '—'}</span>
                          <span className="du-sc-sep">·</span>
                          <span>{s.os || '—'}</span>
                          {s.ipAddress && (
                            <>
                              <span className="du-sc-sep">·</span>
                              <span className="du-sc-ip">{s.ipAddress}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailUser;
